'use client';

import type { IntegrationAuthStatuses } from '@/lib/integrations/auth-status';
import { useQuery } from '@tanstack/react-query';

type IntegrationAuthStatusesResponse = {
  statuses: IntegrationAuthStatuses;
};

export const integrationAuthStatusesRootQueryKey = ['integration-auth-statuses'] as const;

export const integrationAuthStatusesQueryKey = (installationId?: number | null) =>
  [
    ...integrationAuthStatusesRootQueryKey,
    installationId === undefined ? 'disabled' : (installationId ?? 'no-installation'),
  ] as const;

export function useIntegrationAuthStatuses(installationId?: number | null) {
  const { data, isLoading } = useQuery<IntegrationAuthStatusesResponse>({
    queryKey: integrationAuthStatusesQueryKey(installationId),
    enabled: installationId !== undefined,
    queryFn: async () => {
      const params = new URLSearchParams();

      if (installationId != null) {
        params.set('installationId', String(installationId));
      }

      const queryString = params.toString();
      const response = await fetch(
        `/api/integrations/auth-status${queryString ? `?${queryString}` : ''}`,
        { method: 'POST' },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.error || 'Failed to fetch integration auth statuses');
      }

      return response.json() as Promise<IntegrationAuthStatusesResponse>;
    },
    staleTime: 60_000,
  });

  return {
    statuses: data?.statuses ?? {},
    isLoading,
  };
}
